<section class="flex w-full flex-col gap-4">
    <header class="flex w-full flex-wrap items-start justify-between gap-4">
        <div>
            <a [routerLink]="venuesLink()" class="mb-2 inline-flex items-center gap-1.5 text-sm text-text-2 no-underline hover:text-brand">
                <i class="ph ph-arrow-left" aria-hidden="true"></i>
                {{ 'FUNPAY_BACK_TO_VENUES' | translate }}
            </a>
            <h1 class="m-0 font-display text-2xl font-bold text-text">{{ 'FUNPAY_DASHBOARD_TITLE' | translate }}</h1>
            @if (venueName()) {
            <p class="mt-1 text-sm text-text-2">{{ venueName() }}</p>
            }
        </div>

        @if (balanceAccountOptions().length > 1) {
        <p-select
            [options]="balanceAccountOptions()"
            [ngModel]="selectedBalanceAccount()?.id"
            (ngModelChange)="selectBalanceAccount($event)"
            optionLabel="label"
            optionValue="value"
            styleClass="min-w-64" />
        }
    </header>

    @if (loading()) {
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <p-skeleton width="100%" height="8rem" />
        <p-skeleton width="100%" height="8rem" />
        <p-skeleton width="100%" height="8rem" />
    </div>
    <p-skeleton width="100%" height="24rem" />
    } @else if (notLive()) {
    <section class="flex w-full flex-col items-center gap-3 rounded-lg bg-card p-10 text-center">
        <i class="ph ph-hourglass-medium text-3xl text-text-muted" aria-hidden="true"></i>
        <p class="m-0 text-sm text-text-2">{{ 'FUNPAY_ACCOUNT_NOT_LIVE' | translate }}</p>
        <a pButton [routerLink]="onboardingLink()" [outlined]="true">
            <i class="ph ph-arrow-right mr-1.5"></i>
            {{ 'FUNPAY_VIEW_APPLICATION' | translate }}
        </a>
    </section>
    } @else if (loadError()) {
    <section class="flex w-full flex-col items-center gap-3 rounded-lg bg-card p-10 text-center">
        <i class="ph ph-warning-circle text-3xl text-text-muted" aria-hidden="true"></i>
        <p class="m-0 text-sm text-text-2">{{ loadError() | translate }}</p>
        <button pButton type="button" [outlined]="true" (click)="load(id())">
            <i class="ph ph-arrow-clockwise mr-1.5"></i>
            {{ 'FUNPAY_TRY_AGAIN' | translate }}
        </button>
    </section>
    } @else {
    <!-- Account state -->
    @let adyenVerification = verification();
    <section class="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg bg-card px-5 py-3 text-sm">
        <span class="flex items-center gap-2 font-medium text-paid">
            <i class="ph-fill ph-check-circle" aria-hidden="true"></i>
            {{ 'FUNPAY_VERIFICATION_PAYMENTS' | translate }}: {{ 'FUNPAY_VERIFICATION_ALLOWED' | translate }}
        </span>
        <span class="flex items-center gap-2 font-medium" [class]="adyenVerification?.canPayOut ? 'text-paid' : 'text-text-2'">
            <i [class]="adyenVerification?.canPayOut ? 'ph-fill ph-check-circle' : 'ph ph-clock'" aria-hidden="true"></i>
            {{ 'FUNPAY_VERIFICATION_PAYOUTS' | translate }}:
            {{ (adyenVerification?.canPayOut ? 'FUNPAY_VERIFICATION_ALLOWED' : 'FUNPAY_VERIFICATION_PENDING') | translate }}
        </span>
        @if (!dashboard()?.paymentEnabled) {
        <span class="flex items-center gap-2 text-amber-600">
            <i class="ph ph-info" aria-hidden="true"></i>
            {{ 'FUNPAY_PAYMENTS_NOT_ENABLED_ON_VENUE' | translate }}
        </span>
        }
    </section>

    @if (adyenVerification?.problems?.length) {
    <section class="flex flex-col gap-2 rounded-md bg-amber-500/10 p-3">
        <p class="m-0 flex items-center gap-2 text-sm font-medium text-amber-600">
            <i class="ph ph-warning-circle"></i>
            {{ 'FUNPAY_VERIFICATION_PROBLEMS' | translate }}
        </p>
        <ul class="m-0 flex list-disc flex-col gap-1 pl-5 text-sm text-text-2">
            @for (problem of adyenVerification?.problems; track problem) {
            <li>{{ problem }}</li>
            }
        </ul>
    </section>
    }

    <!-- Balances, one row of cards per currency -->
    @for (balance of balances(); track balance.currency) {
    <section class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <article class="flex flex-col gap-1 rounded-lg bg-card p-5">
            <span class="text-sm text-text-muted">{{ 'FUNPAY_BALANCE_AVAILABLE' | translate }}</span>
            <span class="font-display text-3xl font-bold tabular-nums text-text">
                {{ formatMinorUnits(balance.available, balance.currency) }}
            </span>
            <span class="text-xs text-text-muted">{{ 'FUNPAY_BALANCE_AVAILABLE_HINT' | translate }}</span>
        </article>
        <article class="flex flex-col gap-1 rounded-lg bg-card p-5">
            <span class="text-sm text-text-muted">{{ 'FUNPAY_BALANCE_PENDING' | translate }}</span>
            <span class="font-display text-2xl font-semibold tabular-nums text-text">
                {{ formatMinorUnits(balance.pending, balance.currency) }}
            </span>
            <span class="text-xs text-text-muted">{{ 'FUNPAY_BALANCE_PENDING_HINT' | translate }}</span>
        </article>
        <article class="flex flex-col gap-1 rounded-lg bg-card p-5">
            <span class="text-sm text-text-muted">{{ 'FUNPAY_BALANCE_TOTAL' | translate }}</span>
            <span class="font-display text-2xl font-semibold tabular-nums text-text">
                {{ formatMinorUnits(balance.balance, balance.currency) }}
            </span>
            @if (balance.reserved) {
            <span class="text-xs text-text-muted">
                {{ 'FUNPAY_BALANCE_RESERVED' | translate }}: {{ formatMinorUnits(balance.reserved, balance.currency) }}
            </span>
            }
        </article>
    </section>
    } @empty {
    <section class="rounded-lg bg-card p-5 text-sm text-text-2">{{ 'FUNPAY_NO_BALANCES' | translate }}</section>
    }

    <!-- Transactions -->
    <section class="flex flex-col gap-3 rounded-lg bg-card p-5">
        <header class="flex flex-wrap items-center justify-between gap-3">
            <h2 class="m-0 font-display text-lg font-semibold text-text">{{ 'FUNPAY_TRANSACTIONS_TITLE' | translate }}</h2>
            <p-selectbutton
                [options]="windowOptions"
                [ngModel]="transactionWindow()"
                (ngModelChange)="selectWindow($event)"
                optionValue="value"
                [allowEmpty]="false">
                <ng-template #item let-option>{{ option.labelKey | translate }}</ng-template>
            </p-selectbutton>
        </header>

        @if (transactionsError()) {
        <p class="m-0 flex items-center gap-2 rounded-md bg-red-500/10 p-3 text-sm text-red-500">
            <i class="ph ph-warning-circle"></i>
            {{ transactionsError() | translate }}
        </p>
        }

        <p-table [value]="transactions()" [loading]="transactionsLoading() && !transactions().length" dataKey="id">
            <ng-template #header>
                <tr>
                    <th>{{ 'FUNPAY_TRANSACTION_DATE' | translate }}</th>
                    <th>{{ 'FUNPAY_TRANSACTION_TYPE' | translate }}</th>
                    <th>{{ 'FUNPAY_TRANSACTION_DESCRIPTION' | translate }}</th>
                    <th>{{ 'FUNPAY_TRANSACTION_STATUS' | translate }}</th>
                    <th class="text-right">{{ 'FUNPAY_TRANSACTION_AMOUNT' | translate }}</th>
                </tr>
            </ng-template>
            <ng-template #body let-transaction>
                <tr>
                    <td class="whitespace-nowrap text-sm text-text-2">{{ transaction.createdAt | date: 'short' }}</td>
                    <td class="text-sm">{{ categoryKey(transaction) | translate }}</td>
                    <td class="max-w-80 truncate text-sm text-text-2" [title]="transaction.description || transaction.reference">
                        {{ transaction.description || transaction.reference || '—' }}
                    </td>
                    <td>
                        <p-tag
                            [value]="(transaction.status === 'booked' ? 'FUNPAY_TRANSACTION_BOOKED' : 'FUNPAY_TRANSACTION_PENDING') | translate"
                            [severity]="transaction.status === 'booked' ? 'success' : 'warn'"
                            [rounded]="true" />
                    </td>
                    <td
                        class="whitespace-nowrap text-right font-medium tabular-nums"
                        [class]="transaction.amount.value < 0 ? 'text-text' : 'text-paid'">
                        {{ formatMinorUnits(transaction.amount.value, transaction.amount.currency) }}
                    </td>
                </tr>
            </ng-template>
            <ng-template #emptymessage>
                <tr>
                    <td colspan="5" class="py-8 text-center text-sm text-text-muted">{{ 'FUNPAY_NO_TRANSACTIONS' | translate }}</td>
                </tr>
            </ng-template>
        </p-table>

        @if (nextCursor()) {
        <div class="flex justify-center">
            <button
                pButton
                type="button"
                [outlined]="true"
                [loading]="transactionsLoading()"
                [disabled]="transactionsLoading()"
                (click)="loadTransactions()">
                {{ 'FUNPAY_LOAD_MORE' | translate }}
            </button>
        </div>
        }
    </section>
    }
</section>
